import { useEffect, useMemo, useState } from 'react';
import { logoSrc } from '@/crm/contract/branding';
import { Link } from 'react-router-dom';
import { Receipt, Plus, Search, FileDown, Trash2, Edit3, Check, X, AlertCircle, Settings2, FileText, Eye, Undo2, FileSpreadsheet, Wallet, Copy, Lock, Send } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { exportXlsx } from '../utils/exportXlsx';
import { crmStore, useCrm } from '../store';
import { Card, PageTitle } from '../ui';
import { formatEUR, formatDateFR } from '../utils';
import { FACTURE_STATUT_COLORS, type Facture, type FactureLigne, type FactureStatut } from '../types';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useStructureFilter } from '../structureContext';
import { StructureBadge } from '../components/StructureSelector';
import { useConfirm } from '../components/ConfirmDialog';
import { EmetteurEditor } from '../components/EmetteurEditor';
import { AvoirModal } from '../components/AvoirModal';
import { TvaSelect } from '../components/TvaSelect';
import { LignesFactureEditor } from '../components/LignesFactureEditor';
import { EnvoiDocumentModal } from '../components/EnvoiDocumentModal';
import { totauxDocument, estChiffree, echeancierEquilibre, round2 } from '../utils/finance';
import { normalizeAdresse } from '@/crm/utils/adresse';
import { PaiementModal } from '../components/PaiementModal';
import { fetchMentionsActives } from '../components/MentionsDocumentsEditor';
import { controleFinalisation } from '../utils/finance';

/** Numéro de facture attribué côté serveur (sans doublon possible). */
async function fetchNumeroFacture(structure: 'cygnature' | 'suivez_vos_reves'): Promise<string> {
  const { data, error } = await supabase.rpc('next_numero_facture', { _structure: structure });
  if (error || !data) throw new Error(error?.message || 'Numérotation indisponible');
  return data as string;
}

/**
 * Rend le HTML d'aperçu de facture en PDF (visuellement identique).
 * Le PDF visible est ensuite envoyé à l'edge function `generate-facturx`
 * qui y embarque le XML CII (EN 16931) pour produire un PDF/A-3 hybride.
 */
async function renderHtmlToPdfBase64(html: string): Promise<string> {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:820px;height:1200px;border:0;visibility:hidden';
  document.body.appendChild(iframe);
  try {
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();
    // Attendre fonts + images
    await new Promise((r) => setTimeout(r, 250));
    const imgs = Array.from(doc.images);
    await Promise.all(imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((r) => { img.onload = r; img.onerror = r; }))));
    if ((doc as any).fonts?.ready) { try { await (doc as any).fonts.ready; } catch {} }

    const target = doc.body;
    const canvas = await html2canvas(target, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false, windowWidth: 820 });

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= pageH) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgW, imgH);
    } else {
      // Pagination : on découpe le canvas en tranches de la hauteur d'une page A4
      const pxPerMm = canvas.width / pageW;
      const sliceHpx = Math.floor(pageH * pxPerMm);
      let renderedH = 0;
      while (renderedH < canvas.height) {
        const sh = Math.min(sliceHpx, canvas.height - renderedH);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sh;
        const ctx = slice.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, renderedH, canvas.width, sh, 0, 0, canvas.width, sh);
        if (renderedH > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, imgW, (sh * imgW) / canvas.width);
        renderedH += sh;
      }
    }

    const dataUri = pdf.output('datauristring');
    return dataUri.split(',')[1] || '';
  } finally {
    iframe.remove();
  }
}

const STATUTS: FactureStatut[] = ['Brouillon', 'Émise', 'Envoyée', 'Partiellement payée', 'Payée', 'En retard', 'Annulée'];


const MENTIONS_CYGNATURE =
  "TVA acquittée sur les débits. En cas de retard de paiement, pénalités au taux de 3 fois le taux d'intérêt légal et indemnité forfaitaire pour frais de recouvrement de 40 € (Art. L441-10 et D441-5 du Code de commerce). Pas d'escompte pour règlement anticipé.";
const MENTIONS_SVR =
  "TVA non applicable, art. 293 B du CGI. En cas de retard de paiement, pénalités au taux de 3 fois le taux d'intérêt légal et indemnité forfaitaire pour frais de recouvrement de 40 € (Art. L441-10 et D441-5 du Code de commerce). Pas d'escompte pour règlement anticipé.";
/** Mentions administrables (Paramètres > Documents), chargées à l'ouverture de Facturation. */
let mentionsAdmin = '';
fetchMentionsActives().then((t) => { mentionsAdmin = t; }).catch(() => {});
const mentionsLegalesFor = (struct?: string) => {
  if (!mentionsAdmin) return struct === 'suivez_vos_reves' ? MENTIONS_SVR : MENTIONS_CYGNATURE;
  const tva = struct === 'suivez_vos_reves' ? 'TVA non applicable, art. 293 B du CGI.' : 'TVA acquittée sur les débits.';
  return `${tva} ${mentionsAdmin}`;
};

function newFacture(numero: string): Facture {
  const today = new Date().toISOString().slice(0, 10);
  const echeance = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  return {
    id: crypto.randomUUID(),
    numero,
    structure: 'cygnature',
    type: 'Facture',
    prestationId: null,
    clientNom: '',
    clientAdresse: '',
    clientEmail: '',
    clientTel: '',
    clientSiret: '',
    clientTvaIntra: '',
    dateEmission: today,
    dateEcheance: echeance,
    datePrestation: today,
    lignes: [{ description: '', quantite: 1, prixUnitaireHT: 0, tvaPct: 20 }],
    remisePct: 0,
    acompteVerse: 0,
    modePaiement: 'Virement bancaire',
    conditionsPaiement: 'Paiement à 30 jours',
    mentionsLegales: mentionsLegalesFor('cygnature'),
    notes: '',
    statut: 'Brouillon',
    datePaiement: '',
    montantPaye: 0,
    factorXGenere: false,
    chorusProTransmis: false,
    pdpTransmis: false,
    clientPlateformeAgree: '',
    dateCreation: new Date().toISOString(),
  };
}

function calcTotaux(f: Facture) {
  const totalHT = f.lignes.reduce((s, l) => s + (l.quantite || 0) * (l.prixUnitaireHT || 0), 0);
  const remise = totalHT * ((f.remisePct || 0) / 100);
  const htNet = totalHT - remise;
  const tva = f.lignes.reduce((s, l) => {
    const lht = (l.quantite || 0) * (l.prixUnitaireHT || 0);
    const lhtNet = lht * (1 - (f.remisePct || 0) / 100);
    return s + lhtNet * ((l.tvaPct || 0) / 100);
  }, 0);
  const ttc = htNet + tva;
  const restant = ttc - (f.acompteVerse || 0) - (f.montantPaye || 0);
  return { totalHT, remise, htNet, tva, ttc, restant };
}

export default function Facturation() {
  const data = useCrm();
  const confirm = useConfirm();
  const { filter: structureFilter, matches: matchesStructure, setFilter: setStructureFilter } = useStructureFilter();
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState<FactureStatut | 'all'>('all');
  const [editing, setEditing] = useState<Facture | null>(null);
  const [showEmetteur, setShowEmetteur] = useState(false);
  const [avoirFor, setAvoirFor] = useState<Facture | null>(null);

  // Index : pour chaque facture d'origine, l'avoir (le 1er) qui la concerne
  const avoirsByOrigin = useMemo(() => {
    const map = new Map<string, Facture>();
    data.factures.forEach((f) => {
      if (f.type === 'Avoir' && f.avoirOrigineId) {
        const o = data.factures.find((x) => x.id === f.avoirOrigineId);
        if (o) { map.set(o.numero, f); return; }
      }
      if (f.type === 'Avoir' && f.notes) {
        const m = f.notes.match(/Avoir(?: partiel| total)? sur facture ([^\s—\n]+)/);
        if (m) map.set(m[1], f);
      }
    });
    return map;
  }, [data.factures]);

  const factures = useMemo(() => {
    return [...data.factures]
      .filter((f) => matchesStructure(f.structure))
      .filter((f) => statutFilter === 'all' || f.statut === statutFilter)
      .filter((f) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return f.numero.toLowerCase().includes(q) || f.clientNom.toLowerCase().includes(q);
      })
      .sort((a, b) => b.numero.localeCompare(a.numero));
  }, [data.factures, search, statutFilter, structureFilter, matchesStructure]);

  const stats = useMemo(() => {
    const all = data.factures;
    const totalTTC = all.reduce((s, f) => s + calcTotaux(f).ttc, 0);
    const encaisse = all.filter((f) => f.statut === 'Payée').reduce((s, f) => s + calcTotaux(f).ttc, 0);
    const enAttente = all.filter((f) => ['Émise', 'Envoyée', 'Partiellement payée'].includes(f.statut)).reduce((s, f) => s + calcTotaux(f).restant, 0);
    const today = new Date().toISOString().slice(0, 10);
    const enRetard = all.filter((f) => f.statut !== 'Payée' && f.statut !== 'Annulée' && f.dateEcheance < today).length;
    return { totalTTC, encaisse, enAttente, enRetard, count: all.length };
  }, [data.factures]);

  const handleNew = async () => {
    const f = newFacture(''); // numéro attribué à l'enregistrement (pas de trou dans la séquence)
    f.structure = 'cygnature';
    setEditing(f);
  };

  const buildModalitesFromProp = (prop: any): { conditions: string; mode: string } => {
    if (!prop) return { conditions: '', mode: '' };
    const mode = prop.mode_paiement || 'Virement bancaire';
    const overrides = Array.isArray(prop.echeancier_overrides) ? prop.echeancier_overrides : [];
    if (overrides.length) {
      const lignes = overrides
        .map((e: any) => {
          const lbl = e?.label || 'Échéance';
          const date = e?.date ? formatDateFR(e.date) : '';
          const mt = e?.montant != null
            ? `${Number(e.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
            : '';
          return [lbl, date && `le ${date}`, mt].filter(Boolean).join(' — ');
        })
        .filter(Boolean);
      if (lignes.length) return { conditions: lignes.join(' ; ') + ` (par ${mode.toLowerCase()})`, mode };
    }
    const acomptePct = Number(prop.acompte_pct ?? 0);
    const nb = Math.max(1, Number(prop.nb_echeances ?? 1));
    const parts: string[] = [];
    if (acomptePct > 0) parts.push(`${acomptePct}% à la signature`);
    if (nb > 1) {
      const reste = 100 - acomptePct;
      parts.push(`${nb - 1} traite${nb - 1 > 1 ? 's' : ''} de ${(reste / (nb - 1)).toFixed(0)}%`);
      parts.push('solde le jour J');
    } else if (acomptePct > 0) {
      parts.push('solde le jour J');
    } else {
      parts.push('Paiement à 30 jours');
    }
    return { conditions: parts.join(', ') + ` — ${mode.toLowerCase()}`, mode };
  };

  const handleNewFromPrestation = async (prestId: string) => {
    const p = data.prest.find((x) => x.id === prestId);
    if (!p) return;
    const f = newFacture('');
    f.structure = p.structure;
    f.mentionsLegales = mentionsLegalesFor(p.structure);
    f.prestationId = p.id;
    f.clientNom = p.client;
    f.clientEmail = p.email;
    f.clientTel = p.tel;
    f.datePrestation = p.dateEvent;
    f.lignes = [{
      description: `${p.libelle} — ${p.lieu} — ${formatDateFR(p.dateEvent)}\n${p.services.join(', ')}`,
      quantite: 1,
      prixUnitaireHT: p.propTTC ? Math.round(p.propTTC / 1.2) : 0,
      tvaPct: 20,
    }];

    // Résout l'UUID DB de la prestation (p.id côté UI = numero, pas l'UUID DB)
    let prestDbId: string | null = null;
    try {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.id);
      if (isUuid) {
        prestDbId = p.id;
      } else {
        const { data: pr } = await supabase
          .from('prestations')
          .select('id')
          .eq('numero', p.id)
          .maybeSingle();
        prestDbId = pr?.id || null;
      }
    } catch (e) {
      console.warn('[Facturation] prestation UUID lookup failed', e);
    }
    const prestQueryId = prestDbId || p.id;

    // Remontée des infos client complètes (adresse, SIRET, TVA intra…)
    try {
      let clientRow: any = null;
      if (p.clientId) {
        const { data: c } = await supabase
          .from('clients')
          .select('adresse, cp, ville, siret, tva_intra, email, tel, plateforme_agree_dest')
          .eq('id', p.clientId)
          .maybeSingle();
        clientRow = c;
      }
      // Fallback : recherche par nom si pas de clientId ou résultat vide
      if (!clientRow && p.client) {
        const { data: cs } = await supabase
          .from('clients')
          .select('adresse, cp, ville, siret, tva_intra, email, tel, plateforme_agree_dest')
          .ilike('nom', p.client)
          .limit(1);
        clientRow = cs?.[0] || null;
      }
      if (clientRow) {
        const adr = normalizeAdresse(clientRow.adresse, clientRow.cp, clientRow.ville, '\n');
        if (adr) f.clientAdresse = adr;
        if (clientRow.siret) f.clientSiret = clientRow.siret;
        if (clientRow.tva_intra) f.clientTvaIntra = clientRow.tva_intra;
        if (!f.clientEmail && clientRow.email) f.clientEmail = clientRow.email;
        if (!f.clientTel && clientRow.tel) f.clientTel = clientRow.tel;
        if (clientRow.plateforme_agree_dest) f.clientPlateformeAgree = clientRow.plateforme_agree_dest;
      }
    } catch (e) {
      console.warn('[Facturation] client lookup failed', e);
    }

    // Report des modalités + acompte + n° devis + lignes depuis la proposition liée (fallback : contrat)
    try {
      let { data: props } = await supabase
        .from('propositions')
        .select('id, numero, mode_paiement, acompte_pct, acompte_jours, nb_echeances, echeancier_overrides, remise_pct')
        .eq('prestation_id', prestQueryId)
        .order('created_at', { ascending: false })
        .limit(1);
      let prop: any = props?.[0] || null;

      // Fallback : si pas de proposition directe, on récupère via le contrat
      if (!prop) {
        const { data: ctr } = await supabase
          .from('contrats')
          .select('proposition_id, prefix_proposition_origine')
          .eq('prestation_id', prestQueryId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (ctr?.proposition_id) {
          const { data: p2 } = await supabase
            .from('propositions')
            .select('id, numero, mode_paiement, acompte_pct, acompte_jours, nb_echeances, echeancier_overrides, remise_pct')
            .eq('id', ctr.proposition_id)
            .maybeSingle();
          if (p2) prop = p2;
        }
        if (!prop && ctr?.prefix_proposition_origine) {
          f.prefixDevisOrigine = ctr.prefix_proposition_origine;
        }
      }

      // Fallback : aller chercher le devis (table legacy) si toujours rien
      if (!prop) {
        const { data: dv } = await supabase
          .from('devis')
          .select('numero, mode_paiement, acompte_pct')
          .eq('prestation_id', prestQueryId)
          .order('date_emission', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (dv) {
          prop = { numero: dv.numero, mode_paiement: dv.mode_paiement, acompte_pct: dv.acompte_pct, acompte_jours: 15, nb_echeances: 1, echeancier_overrides: null };
        }
      }

      if (prop) {
        if (prop.numero) f.prefixDevisOrigine = prop.numero;
        const { conditions, mode } = buildModalitesFromProp(prop);
        if (conditions) f.conditionsPaiement = conditions;
        if (mode) f.modePaiement = mode;
        if (prop.remise_pct != null) f.remisePct = Number(prop.remise_pct) || 0;

        // Remontée des lignes de la proposition (titre/description, qty, PU HT)
        if (prop.id) {
          const { data: lignes } = await supabase
            .from('propositions_lignes')
            .select('ordre, titre, description, quantite, prix_unitaire_ht')
            .eq('proposition_id', prop.id)
            .order('ordre', { ascending: true });
          if (lignes && lignes.length) {
            f.lignes = lignes.map((l: any) => {
              const desc = [l.titre, l.description].filter((s: string) => s && String(s).trim()).join(' — ');
              return {
                description: desc || `${p.libelle} — ${p.lieu} — ${formatDateFR(p.dateEvent)}`,
                quantite: Number(l.quantite) || 1,
                prixUnitaireHT: Number(l.prix_unitaire_ht) || 0,
                tvaPct: 20,
              };
            });
          }
        }

        // Construction de l'échéancier — overrides si présents, sinon dérivé de acompte_pct / nb_echeances
        const overrides: any[] = Array.isArray(prop.echeancier_overrides) ? (prop.echeancier_overrides as any[]) : [];
        const ttcCalc = calcTotaux(f).ttc;
        const acomptePct = Math.max(0, Math.min(100, Number(prop.acompte_pct ?? 0)));
        const nbEch = Math.max(1, Math.min(24, Number(prop.nb_echeances ?? 1)));
        const acompteJours = Math.max(0, Number(prop.acompte_jours ?? 0));

        if (overrides.length) {
          f.echeancier = overrides.map((o: any) => ({
            label: o?.label ?? undefined,
            date: o?.date ?? undefined,
            montant: o?.montant != null ? Number(o.montant) : null,
          }));
        } else if (ttcCalc > 0 && (acomptePct > 0 || nbEch > 1)) {
          // Génère un échéancier sur la base du TTC
          const acompte = Math.round((ttcCalc * acomptePct) / 100);
          const solde = Math.max(0, ttcCalc - acompte);
          const refDate = p.dateEvent ? new Date(p.dateEvent) : new Date();
          const rows: { label: string; date: string; montant: number }[] = [];
          if (acompte > 0) {
            const aDate = new Date();
            aDate.setDate(aDate.getDate() + acompteJours);
            rows.push({ label: `Acompte (${acomptePct}%)`, date: aDate.toISOString().slice(0, 10), montant: acompte });
          }
          let cumul = 0;
          const base = solde / nbEch;
          for (let i = 1; i <= nbEch; i++) {
            const d = new Date(refDate);
            d.setMonth(d.getMonth() + (i - nbEch));
            const isLast = i === nbEch;
            const montant = isLast ? Math.round(solde - cumul) : Math.round(base);
            cumul += montant;
            const label = isLast ? 'Solde' : (nbEch > 1 ? `Traite ${i}/${nbEch - 1}` : 'Solde');
            rows.push({ label, date: d.toISOString().slice(0, 10), montant });
          }
          f.echeancier = rows;
        }

        const firstMontant = overrides[0] && typeof overrides[0] === 'object' ? (overrides[0] as any).montant : null;
        if (firstMontant != null) {
          f.acompteVerse = Number(firstMontant) || 0;
        } else if (acomptePct > 0 && ttcCalc) {
          f.acompteVerse = Math.round((ttcCalc * acomptePct) / 100);
        } else if (acomptePct > 0 && p.propTTC) {
          f.acompteVerse = Math.round((p.propTTC * acomptePct) / 100);
        }
      }

    } catch (e) {
      console.warn('[Facturation] proposition/contrat lookup failed', e);
    }
    setEditing(f);
  };

  const handleSave = async () => {
    if (!editing) return;
    return saveFacture(editing);
  };

  const saveFacture = async (editing: Facture) => {
    if (!editing.clientNom.trim()) { toast.error('Nom du client obligatoire'); return; }

    // Verrouillage : une facture déjà finalisée ne peut plus changer de contenu
    const original = data.factures.find((x) => x.id === editing.id);
    if (original && original.statut !== 'Brouillon') {
      const contenu = (f: Facture) => JSON.stringify([f.clientNom, f.clientAdresse, f.lignes, f.remisePct, f.dateEmission, f.structure, f.type]);
      if (contenu(original) !== contenu(editing)) {
        toast.error(`Facture ${original.numero} finalisée : son contenu ne peut plus être modifié. Créez un avoir ou dupliquez-la en nouveau brouillon.`, { duration: 8000 });
        return;
      }
    }
    // Contrôle avant finalisation
    if (editing.statut !== 'Brouillon') {
      const manque = controleFinalisation({ ...editing, numero: editing.numero || 'à attribuer' });
      if (manque.length) {
        toast.error(`Impossible de finaliser : une information obligatoire manque — ${manque.join(', ')}.`, { duration: 9000 });
        return;
      }
    }
    // Échéancier : le total des échéances doit correspondre au TTC
    if (editing.echeancier && editing.echeancier.length) {
      const ttcF = totauxDocument(editing.lignes, editing.remisePct).ttc;
      const mts = editing.echeancier.map((e) => Number(e.montant) || 0);
      if (!echeancierEquilibre(mts, ttcF) && !echeancierEquilibre(mts, ttcF - (editing.acompteVerse || 0))) {
        toast.error(`Échéancier incorrect : le total des échéances ne correspond pas au TTC (${formatEUR(ttcF)}). Utilisez « Répartir le solde ».`, { duration: 9000 });
        return;
      }
    }
    // Validation émetteur — obligatoire dès qu'on quitte le statut Brouillon
    if (editing.statut !== 'Brouillon') {
      const em = getEmetteur(editing.structure);
      const missing: string[] = [];
      if (!em.raisonSociale) missing.push('Raison sociale');
      if (!em.adresse) missing.push('Adresse');
      if (!em.siret) missing.push('SIRET');
      if (!em.tvaIntra) missing.push('N° TVA intra');
      if (!em.iban) missing.push('IBAN');
      if (!em.bic) missing.push('BIC');
      if (!em.rcs) missing.push('RCS');
      if (!em.capital) missing.push('Capital');
      if (!em.ape) missing.push('Code APE');
      if (missing.length > 0) {
        toast.error(
          `Émetteur incomplet : ${missing.join(', ')}. Complétez-le via Paramètres › Émetteur, ou enregistrez en « Brouillon ».`,
          { duration: 8000 },
        );
        return;
      }
    }

    // Numéro attribué au dernier moment, une fois tous les contrôles passés
    if (!editing.numero) {
      try { editing = { ...editing, numero: await fetchNumeroFacture(editing.structure) }; }
      catch (e: any) { toast.error(`Numérotation impossible : ${e.message}`); return; }
    }
    try {
      const savedStructure = editing.structure;
      await crmStore.upsertFacture(editing);
      toast.success(`Facture ${editing.numero} enregistrée`);
      setSearch('');
      setStatutFilter('all');
      if (!matchesStructure(savedStructure)) setStructureFilter(savedStructure);
      setEditing(null);
    } catch (e: any) {
      console.error('[Facturation] save failed', e);
      toast.error(`Erreur d'enregistrement : ${e?.message || 'inconnue'}`);
    }
  };

  const handleDelete = async (f: Facture) => {
    // Protection : un avoir déjà comptabilisé (≠ Brouillon) ne se supprime pas
    if (f.type === 'Avoir' && f.statut !== 'Brouillon') {
      toast.error(`Avoir « ${f.statut} » : suppression interdite. Émettez plutôt une facture corrective.`);
      return;
    }
    if (f.type === 'Facture' && f.statut !== 'Brouillon' && f.statut !== 'Annulée') {
      const okHard = await confirm({
        title: `Supprimer la facture ${f.numero} ?`,
        description: `Cette facture est au statut « ${f.statut} ». En compta française, on émet plutôt un avoir. Confirmer quand même ?`,
        confirmLabel: 'Supprimer quand même',
        destructive: true,
      });
      if (!okHard) return;
    } else {
      const ok = await confirm({
        title: 'Supprimer cette facture ?',
        description: 'Attention : le numéro restera consommé pour respecter la séquentialité légale.',
        confirmLabel: 'Supprimer',
        destructive: true,
      });
      if (!ok) return;
    }
    crmStore.removeFacture(f.id);
    toast.success('Facture supprimée');
  };

  const handleCreateAvoir = (f: Facture) => {
    if (f.type === 'Avoir') {
      toast.error('Un avoir ne peut pas avoir d\'avoir');
      return;
    }
    if (f.statut === 'Brouillon') {
      toast.error('Un avoir se crée sur une facture finalisée. Un brouillon se modifie ou se supprime directement.');
      return;
    }
    if (avoirsByOrigin.has(f.numero)) {
      toast.error(`Un avoir existe déjà pour ${f.numero} (${avoirsByOrigin.get(f.numero)!.numero}).`);
      return;
    }
    setAvoirFor(f);
  };

  const handleConfirmAvoir = async (origin: Facture, lignes: FactureLigne[], motif: string) => {
    const { data: avNum, error: avErr } = await supabase.rpc('next_numero_avoir');
    if (avErr || !avNum) { toast.error(`Numérotation de l'avoir impossible : ${avErr?.message || ''}`); return; }
    const numero = avNum as string;
    const today = new Date().toISOString().slice(0, 10);
    const isPartiel = lignes.length !== origin.lignes.length
      || lignes.some((l, i) => Math.abs(l.quantite) !== Math.abs(origin.lignes[i]?.quantite ?? 0));
    const notesParts = [
      `${isPartiel ? 'Avoir partiel' : 'Avoir total'} sur facture ${origin.numero}`,
      motif ? `Motif : ${motif}` : null,
    ].filter(Boolean);
    const avoir: Facture = {
      ...origin,
      id: crypto.randomUUID(),
      numero,
      type: 'Avoir',
      statut: 'Brouillon',
      dateEmission: today,
      dateEcheance: today,
      datePaiement: '',
      montantPaye: 0,
      acompteVerse: 0,
      factorXGenere: false,
      pdpTransmis: false,
      chorusProTransmis: false,
      notes: notesParts.join('\n'),
      lignes,
      avoirOrigineId: origin.id,
      finaliseeAt: null,
      dateCreation: new Date().toISOString(),
    };
    try {
      await crmStore.upsertFacture(avoir);
      toast.success(`Avoir ${numero} créé`);
      setAvoirFor(null);
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || 'création impossible'}`);
    }
  };



  const handleDuplicate = async (f: Facture) => {
    const today = new Date().toISOString().slice(0, 10);
    setEditing({
      ...f, id: crypto.randomUUID(), numero: '', type: f.type === 'Avoir' ? 'Facture' : f.type, statut: 'Brouillon',
      dateEmission: today, dateEcheance: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      datePaiement: '', montantPaye: 0, factorXGenere: false, pdpTransmis: false, chorusProTransmis: false,
      avoirOrigineId: null, finaliseeAt: null, dateCreation: new Date().toISOString(),
      echeancier: f.echeancier?.map((e) => ({ ...e, paid: false, paid_at: undefined })),
    });
    toast.message("Copie en brouillon — vous pouvez changer le client ; le numéro sera attribué à l'enregistrement.");
  };

  const [paiementFor, setPaiementFor] = useState<Facture | null>(null);
  const totalDuApresAvoir = (f: Facture) => {
    const t = calcTotaux(f);
    const av = avoirsByOrigin.get(f.numero);
    return Math.round((t.ttc - (f.acompteVerse || 0) - (av ? Math.abs(calcTotaux(av).ttc) : 0)) * 100) / 100;
  };

  const handleStatutChange = (f: Facture, statut: FactureStatut) => {
    if (f.statut === 'Brouillon' && statut !== 'Brouillon') {
      const manque = controleFinalisation(f);
      if (manque.length) {
        toast.error(`Impossible de finaliser : une information obligatoire manque — ${manque.join(', ')}.`, { duration: 9000 });
        return;
      }
    }
    if (f.statut !== 'Brouillon' && statut === 'Brouillon') {
      toast.error('Une facture finalisée ne peut pas revenir en brouillon. Dupliquez-la pour repartir d’un brouillon.');
      return;
    }
    const updated = { ...f, statut };
    if (statut === 'Payée' && !f.datePaiement) {
      updated.datePaiement = new Date().toISOString().slice(0, 10);
      updated.montantPaye = calcTotaux(f).ttc;
    }
    crmStore.upsertFacture(updated);
  };

  const getEmetteur = (struct?: string) => {
    const map = (data as any).emetteurs || {};
    return map[struct || 'cygnature'] || data.emetteur;
  };

  const [envoi, setEnvoi] = useState<Facture | null>(null);

  const handleApercu = (f: Facture) => {
    const html = renderInvoiceHTML(f, getEmetteur(f.structure));
    const w = window.open('', '_blank');
    if (!w) { toast.error('Bloqué par le navigateur'); return; }
    w.document.write(html);
    w.document.close();
  };


  const handleGenerateFacturX = async (f: Facture) => {
    const emetteurF = getEmetteur(f.structure);
    if (!emetteurF.siret) {
      toast.error('SIRET émetteur obligatoire — paramétrez-le via le bouton Émetteur.');
      return;
    }
    if (!f.clientNom) {
      toast.error('Nom client manquant.');
      return;
    }
    const toastId = toast.loading('Génération du Factur-X (PDF/A-3 + XML CII)…');
    try {
      // 1) On rend le PDF visible à partir EXACTEMENT du même HTML que l'aperçu
      const html = renderInvoiceHTML(f, emetteurF);
      const pdfBase64Visible = await renderHtmlToPdfBase64(html);

      const payload = {
        facture: {
          numero: f.numero,
          type: f.type,
          dateEmission: f.dateEmission,
          dateEcheance: f.dateEcheance,
          datePrestation: f.datePrestation,
          clientNom: f.clientNom,
          clientAdresse: f.clientAdresse,
          clientEmail: f.clientEmail,
          clientSiret: f.clientSiret,
          clientTvaIntra: f.clientTvaIntra,
          lignes: f.lignes,
          remisePct: f.remisePct,
          acompteVerse: f.acompteVerse,
          modePaiement: f.modePaiement,
          conditionsPaiement: f.conditionsPaiement,
          mentionsLegales: f.mentionsLegales,
          notes: f.notes,
        },
        emetteur: emetteurF,
        // 2) L'edge function ne fait QUE générer le XML et l'embarquer dans ce PDF
        pdf_base64: pdfBase64Visible,
      };
      const { data: res, error } = await supabase.functions.invoke('generate-facturx', { body: payload });
      if (error) throw error;
      if (!res?.success) throw new Error(res?.error || 'Réponse invalide');

      // Téléchargement du PDF/A-3
      const bin = atob(res.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.filename; a.click();
      URL.revokeObjectURL(url);

      // Persistance : archive légale (hash + date) directement en BDD
      await supabase.from('factures').update({
        format_facturx: true,
        archive_legal_hash: res.sha256,
        archive_legal_date: new Date().toISOString(),
      }).eq('numero', f.numero);

      crmStore.reload();
      toast.success(`Factur-X généré (${(res.size / 1024).toFixed(1)} Ko) — Archivage légal validé`, { id: toastId });
    } catch (e: any) {
      console.error('[Factur-X]', e);
      toast.error('Échec génération : ' + (e.message || 'inconnu'), { id: toastId });
    }
  };

  return (
    <div>
      <PageTitle
        title="Facturation électronique"
        subtitle="Génération, édition et suivi des factures — Conforme à l'obligation 2026"
        icon={<Receipt className="text-amber-400" size={24} strokeWidth={1.5} />}
        action={
          <div className="flex gap-2">
            <button
              onClick={() => {
                const stamp = new Date().toISOString().slice(0, 10);
                const rows = factures.map((f) => {
                  const t = calcTotaux(f);
                  return {
                    numero: f.numero,
                    type: f.type,
                    statut: f.statut,
                    structure: f.structure,
                    clientNom: f.clientNom,
                    clientEmail: f.clientEmail || '',
                    clientSiret: f.clientSiret || '',
                    dateEmission: f.dateEmission,
                    dateEcheance: f.dateEcheance,
                    datePrestation: f.datePrestation || '',
                    datePaiement: f.datePaiement || '',
                    totalHT: t.htNet,
                    totalTVA: t.tva,
                    totalTTC: t.ttc,
                    acompteVerse: f.acompteVerse || 0,
                    montantPaye: f.montantPaye || 0,
                    restant: t.restant,
                    modePaiement: f.modePaiement || '',
                    factorXGenere: f.factorXGenere ? 'Oui' : 'Non',
                  };
                });
                exportXlsx(`factures-${stamp}.xlsx`, rows, [
                  { header: 'N° Facture', key: 'numero' as any, width: 14 },
                  { header: 'Type', key: 'type' as any, width: 10 },
                  { header: 'Statut', key: 'statut' as any, width: 18 },
                  { header: 'Structure', key: 'structure' as any, width: 16 },
                  { header: 'Client', key: 'clientNom' as any, width: 28 },
                  { header: 'Email', key: 'clientEmail' as any, width: 26 },
                  { header: 'SIRET', key: 'clientSiret' as any, width: 18 },
                  { header: 'Émise le', key: 'dateEmission' as any, format: 'date', width: 12 },
                  { header: 'Échéance', key: 'dateEcheance' as any, format: 'date', width: 12 },
                  { header: 'Prestation', key: 'datePrestation' as any, format: 'date', width: 12 },
                  { header: 'Payée le', key: 'datePaiement' as any, format: 'date', width: 12 },
                  { header: 'Total HT', key: 'totalHT' as any, format: 'currency', width: 14 },
                  { header: 'TVA', key: 'totalTVA' as any, format: 'currency', width: 14 },
                  { header: 'Total TTC', key: 'totalTTC' as any, format: 'currency', width: 14 },
                  { header: 'Acompte versé', key: 'acompteVerse' as any, format: 'currency', width: 14 },
                  { header: 'Montant payé', key: 'montantPaye' as any, format: 'currency', width: 14 },
                  { header: 'Restant dû', key: 'restant' as any, format: 'currency', width: 14 },
                  { header: 'Mode paiement', key: 'modePaiement' as any, width: 18 },
                  { header: 'Factur-X', key: 'factorXGenere' as any, width: 10 },
                ], 'Factures');
              }}
              title="Exporter en Excel"
              className="flex items-center gap-2 px-3 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 rounded-lg text-xs"
            >
              <FileSpreadsheet size={14} strokeWidth={1.5} /> Excel
            </button>
            <Link to="../export-comptable" className="flex items-center gap-2 px-3 py-2 bg-violet-500/15 hover:bg-violet-500/25 text-violet-300 border border-violet-500/40 rounded-lg text-xs">
              <FileDown size={14} strokeWidth={1.5} /> Export comptable
            </Link>
            <button onClick={() => setShowEmetteur(true)} className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs text-slate-200">
              <Settings2 size={14} strokeWidth={1.5} /> Émetteur
            </button>
            <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-sm font-medium">
              <Plus size={16} strokeWidth={1.5} /> Nouvelle facture
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Total factures</div>
          <div className="text-2xl font-heading text-slate-100">{stats.count}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">CA TTC émis</div>
          <div className="text-2xl font-heading text-amber-400">{formatEUR(stats.totalTTC)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Encaissé</div>
          <div className="text-2xl font-heading text-emerald-400">{formatEUR(stats.encaisse)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">En attente</div>
          <div className="text-2xl font-heading text-sky-400">{formatEUR(stats.enAttente)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">En retard</div>
          <div className="text-2xl font-heading text-red-400 flex items-center gap-2">
            {stats.enRetard > 0 && <AlertCircle size={20} strokeWidth={1.5} />}
            {stats.enRetard}
          </div>
        </Card>
      </div>

      {/* Compliance banner */}
      <Card className="p-4 mb-6 border-amber-500/30 bg-amber-500/5">
        <div className="flex items-start gap-3 text-xs text-slate-300">
          <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={16} strokeWidth={1.5} />
          <div>
            <div className="font-medium text-amber-300 mb-1">Facturation électronique — Calendrier de l'obligation</div>
            <p className="text-slate-400">
              Réception obligatoire pour toutes les entreprises : <strong className="text-slate-200">1er septembre 2026</strong>. Émission obligatoire :
              grandes/moyennes entreprises au <strong className="text-slate-200">1er sept. 2026</strong>, TPE/PME au <strong className="text-slate-200">1er septembre 2027</strong>.
              Les factures B2B doivent transiter par une <strong className="text-slate-200">Plateforme de Dématérialisation Partenaire (PDP)</strong> au format Factur-X, UBL ou CII.
            </p>
          </div>
        </div>
      </Card>

      {/* Filters + create from prestation */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher numéro ou client…"
              className="w-full pl-9 pr-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value as any)}
            className="px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 [&>option]:bg-slate-950 [&>option]:text-slate-200"
          >
            <option value="all">Tous les statuts</option>
            {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            onChange={(e) => { if (e.target.value) { handleNewFromPrestation(e.target.value); e.target.value = ''; } }}
            className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-sm text-emerald-300 [&>option]:bg-slate-950 [&>option]:text-slate-200"
          >
            <option value="" className="bg-slate-950 text-slate-200">+ Créer depuis prestation…</option>
              {data.prest.filter((p) => {
                if (!matchesStructure(p.structure)) return false;
                if (p.archivedAt) return false;
                const s = p.statut || '';
                if (/Refus|Annul/i.test(s)) return false;
                return /Signé|Contrat|Propal|Relance|Devis|Réalis|Payé/i.test(s);
              }).map((p) => (
              <option key={p.id} value={p.id} className="bg-slate-950 text-slate-200">{p.id} — {p.client} — {formatDateFR(p.dateEvent)}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-4 py-3">Numéro</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Émission</th>
                <th className="text-left px-4 py-3">Échéance</th>
                <th className="text-right px-4 py-3">Total TTC</th>
                <th className="text-right px-4 py-3">Restant</th>
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-center px-4 py-3">e-invoice</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {factures.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-slate-500">Aucune facture. Créez la première via le bouton ci-dessus.</td></tr>
              )}
              {factures.map((f) => {
                const t = calcTotaux(f);
                const cls = FACTURE_STATUT_COLORS[f.statut];
                const linkedAvoir = f.type !== 'Avoir' ? avoirsByOrigin.get(f.numero) : null;
                const originNumero = f.type === 'Avoir' ? (f.notes?.match(/Avoir(?: partiel| total)? sur facture ([^\s—\n]+)/)?.[1] || null) : null;
                return (
                  <tr key={f.id} className="border-t border-slate-800 hover:bg-slate-900/40">
                    <td className="px-4 py-3 font-mono text-xs text-amber-300">
                      <div className="flex items-center gap-2">
                        <StructureBadge code={f.structure} />
                        {f.numero}
                        {f.type === 'Avoir' && <span className="px-1.5 py-0.5 bg-orange-500/15 text-orange-300 rounded text-[10px]">AVOIR</span>}
                        {f.type === 'Acompte' && <span className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 rounded text-[10px]">ACOMPTE</span>}
                      </div>
                      {f.prefixDevisOrigine && <div className="text-[10px] text-violet-300 font-normal mt-0.5" title="Devis d'origine">↳ {f.prefixDevisOrigine}</div>}
                      {linkedAvoir && <div className="text-[10px] text-orange-300 font-normal mt-0.5" title="Avoir lié">↩ Avoir : {linkedAvoir.numero}</div>}
                      {originNumero && <div className="text-[10px] text-slate-400 font-normal mt-0.5" title="Facture d'origine">↳ sur {originNumero}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-200">{f.clientNom || <span className="text-slate-500">—</span>}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{formatDateFR(f.dateEmission)}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{formatDateFR(f.dateEcheance)}</td>
                    <td className="px-4 py-3 text-right text-slate-200 font-medium">{formatEUR(t.ttc)}</td>
                    <td className="px-4 py-3 text-right text-slate-300" title={linkedAvoir ? `Montant initial ${formatEUR(t.ttc)} — Avoir ${linkedAvoir.numero} −${formatEUR(Math.abs(calcTotaux(linkedAvoir).ttc))}` : undefined}>
                      {formatEUR(linkedAvoir ? t.restant - Math.abs(calcTotaux(linkedAvoir).ttc) : t.restant)}
                      {linkedAvoir && <div className="text-[10px] text-orange-300">solde après avoir</div>}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={f.statut}
                        onChange={(e) => handleStatutChange(f, e.target.value as FactureStatut)}
                        className={`px-2 py-1 rounded-md border text-xs bg-transparent ${cls}`}
                      >
                        {STATUTS.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                      </select>
                      {f.type !== 'Avoir' && !['Payée', 'Annulée', 'Brouillon', 'En retard'].includes(f.statut) && f.dateEcheance && f.dateEcheance < new Date().toISOString().slice(0, 10) && (
                        <div className="text-[10px] text-red-400 mt-1">Échue</div>
                      )}
                      {f.statut !== 'Brouillon' && <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1"><Lock size={10} strokeWidth={1.5} /> finalisée</div>}
                    </td>
                    <td className="px-4 py-3 text-center text-xs">
                      <div className="flex justify-center gap-1">
                        {f.factorXGenere && <span title={`Factur-X généré${(f as any).archiveLegalHash ? ' — Archive : ' + ((f as any).archiveLegalHash as string).slice(0, 12) + '…' : ''}`} className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 rounded">FX</span>}
                        {f.pdpTransmis && <span title="PDP transmis" className="px-1.5 py-0.5 bg-violet-500/15 text-violet-300 rounded">PDP</span>}
                        {f.chorusProTransmis && <span title="Chorus Pro" className="px-1.5 py-0.5 bg-sky-500/15 text-sky-300 rounded">CP</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditing(f)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-400" title="Éditer">
                          <Edit3 size={14} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => handleApercu(f)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-sky-400" title="Aperçu HTML">
                          <Eye size={14} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => { if (!f.numero || f.statut === 'Brouillon') { toast.error('Finalisez la facture avant de l’envoyer'); return; } setEnvoi(f); }} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-300" title="Envoyer par e-mail">
                          <Send size={14} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => handleGenerateFacturX(f)} className="p-1.5 hover:bg-slate-800 rounded text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" title="Télécharger PDF (Factur-X)">
                          <FileDown size={14} strokeWidth={1.5} />
                        </button>
                        {f.type !== 'Avoir' && f.statut !== 'Brouillon' && f.statut !== 'Annulée' && (
                          <button onClick={() => setPaiementFor(f)} className="p-1.5 hover:bg-emerald-500/10 rounded text-emerald-400" title="Enregistrer un paiement">
                            <Wallet size={14} strokeWidth={1.5} />
                          </button>
                        )}
                        <button onClick={() => handleDuplicate(f)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-400" title="Dupliquer en nouveau brouillon">
                          <Copy size={14} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => handleCreateAvoir(f)} disabled={f.type === 'Avoir' || !!linkedAvoir} className="p-1.5 hover:bg-orange-500/10 rounded text-orange-400 disabled:opacity-30 disabled:cursor-not-allowed" title={f.type === 'Avoir' ? 'Pas d\u2019avoir sur un avoir' : linkedAvoir ? `Avoir existant : ${linkedAvoir.numero}` : 'Créer un avoir (total ou partiel)'}>
                          <Undo2 size={14} strokeWidth={1.5} />
                        </button>
                        <button onClick={() => handleDelete(f)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-red-400" title="Supprimer">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {envoi && (
        <EnvoiDocumentModal
          html={renderInvoiceHTML(envoi, getEmetteur(envoi.structure))}
          fileName={`${envoi.type === 'Avoir' ? 'Avoir' : 'Facture'}-${envoi.numero}.pdf`}
          libelle={`${envoi.type === 'Avoir' ? "l'avoir" : 'la facture'} ${envoi.numero}`}
          defaultTo={envoi.clientEmail}
          defaultSujet={`${envoi.type === 'Avoir' ? 'Avoir' : 'Facture'} ${envoi.numero} — ${envoi.structure === 'suivez_vos_reves' ? 'Suivez Vos Rêves Event' : 'Cygnature Production'}`}
          defaultMessage={`Bonjour,\n\nVeuillez trouver ci-joint ${envoi.type === 'Avoir' ? "l'avoir" : 'la facture'} ${envoi.numero}, d'un montant de ${formatEUR(calcTotaux(envoi).ttc)} TTC${envoi.type !== 'Avoir' && envoi.dateEcheance ? `, à régler avant le ${new Date(envoi.dateEcheance).toLocaleDateString('fr-FR')}` : ''}.\n\nNous restons à votre disposition pour toute question.\n\nBien cordialement,\nCyrille LECOURT`}
          onClose={() => setEnvoi(null)}
          onSent={async () => { if (envoi.statut === 'Émise') { await supabase.from('factures').update({ statut: 'Envoyée' }).eq('id', envoi.id); await crmStore.reload(); } }}
        />
      )}
      {editing && <FactureEditor facture={editing} emetteur={getEmetteur(editing.structure)} onChange={setEditing} onSave={handleSave} onCancel={() => setEditing(null)} paiementModes={data.paiementModes} />}
      {paiementFor && <PaiementModal facture={paiementFor} totalDu={totalDuApresAvoir(paiementFor)} modes={data.paiementModes || []} onClose={() => setPaiementFor(null)} />}
      {showEmetteur && <EmetteurEditor onClose={() => setShowEmetteur(false)} />}
      {avoirFor && <AvoirModal facture={avoirFor} onCancel={() => setAvoirFor(null)} onConfirm={(l, m) => handleConfirmAvoir(avoirFor, l, m)} />}
    </div>
  );
}


/* ---------- Editor modal ---------- */

function FactureEditor({ facture, emetteur, onChange, onSave, onCancel, paiementModes }: {
  facture: Facture; emetteur: import('../types').EmetteurInfo; onChange: (f: Facture) => void; onSave: () => void; onCancel: () => void; paiementModes: string[];
}) {
  const t = calcTotaux(facture);
  const upd = (patch: Partial<Facture>) => onChange({ ...facture, ...patch });
  const updLigne = (i: number, patch: Partial<FactureLigne>) => {
    const lignes = facture.lignes.map((l, idx) => idx === i ? { ...l, ...patch } : l);
    onChange({ ...facture, lignes });
  };
  const addLigne = () => onChange({ ...facture, lignes: [...facture.lignes, { description: '', quantite: 1, prixUnitaireHT: 0, tvaPct: 20 }] });
  const removeLigne = (i: number) => onChange({ ...facture, lignes: facture.lignes.filter((_, idx) => idx !== i) });

  const echeancier = facture.echeancier || [];
  const updEch = (i: number, patch: Partial<{ label: string; date: string; montant: number | null }>) => {
    const next = echeancier.map((e, idx) => idx === i ? { ...e, ...patch } : e);
    onChange({ ...facture, echeancier: next });
  };
  const addEch = () => onChange({
    ...facture,
    echeancier: [...echeancier, { label: 'Échéance', date: new Date().toISOString().slice(0, 10), montant: 0 }],
  });
  const removeEch = (i: number) => onChange({ ...facture, echeancier: echeancier.filter((_, idx) => idx !== i) });
  const resetEch = () => onChange({ ...facture, echeancier: undefined });
  const echTotal = echeancier.reduce((s, e) => s + (Number(e.montant) || 0), 0);
  const [showPreview, setShowPreview] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  useEffect(() => {
    if (!showPreview) return;
    const id = setTimeout(() => { try { setPreviewHtml(renderInvoiceHTML(facture, emetteur)); } catch { /* aperçu indisponible */ } }, 250);
    return () => clearTimeout(id);
  }, [facture, emetteur, showPreview]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto gap-4">
      <div className={`bg-slate-900 border border-slate-800 rounded-xl w-full my-8 ${showPreview ? 'max-w-4xl' : 'max-w-5xl'}`}>
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900 rounded-t-xl">
          <h2 className="text-lg font-heading text-slate-100 flex items-center gap-3 flex-wrap">
            <FileText className="text-amber-400" size={20} strokeWidth={1.5} />
            Facture <span className="font-mono text-amber-300">{facture.numero || "(numéro attribué à l’enregistrement)"}</span>
            {facture.prefixDevisOrigine && (
              <span className="text-xs font-sans text-slate-400 normal-case">
                · Réf. devis <span className="font-mono text-slate-200">{facture.prefixDevisOrigine}</span>
              </span>
            )}
          </h2>
          <div className="flex gap-2">
            <button onClick={() => setShowPreview((v) => !v)} className="hidden xl:inline-flex items-center gap-1 px-3 py-1.5 text-sm text-slate-300 border border-slate-700 rounded-md hover:bg-slate-800">
              <Eye size={14} strokeWidth={1.5} /> {showPreview ? 'Masquer l’aperçu' : 'Aperçu en direct'}
            </button>
            <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200">Annuler</button>
            <button onClick={onSave} className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-md text-sm font-medium">
              <Check size={14} strokeWidth={1.5} /> Enregistrer
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Type + dates */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Field label="Structure émettrice *">
              <select value={facture.structure || 'cygnature'} onChange={(e) => { const s = e.target.value as any; upd({ structure: s, mentionsLegales: mentionsLegalesFor(s) }); }} className={selectCls}>
                <option value="cygnature">Cygnature Production (SASU)</option>
                <option value="suivez_vos_reves">Suivez Vos Rêves Event (Asso)</option>
              </select>
            </Field>
            <Field label="Type">
              <select value={facture.type} onChange={(e) => upd({ type: e.target.value as any })} className={selectCls}>
                <option value="Facture">Facture</option>
                <option value="Acompte">Facture d'acompte</option>
                <option value="Intermédiaire">Facture intermédiaire</option>
                <option value="Solde">Facture de solde</option>
                <option value="Avoir">Avoir</option>
              </select>
            </Field>
            <Field label="Date d'émission"><input type="date" value={facture.dateEmission} onChange={(e) => upd({ dateEmission: e.target.value })} className={inputCls} /></Field>
            <Field label="Date d'échéance"><input type="date" value={facture.dateEcheance} onChange={(e) => upd({ dateEcheance: e.target.value })} className={inputCls} /></Field>
            <Field label="Date de prestation"><input type="date" value={facture.datePrestation} onChange={(e) => upd({ datePrestation: e.target.value })} className={inputCls} /></Field>
          </div>

          {/* Client */}
          <div>
            <h3 className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Client (destinataire)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Raison sociale / Nom *"><input value={facture.clientNom} onChange={(e) => upd({ clientNom: e.target.value })} className={inputCls} /></Field>
              <Field label="Email"><input type="email" value={facture.clientEmail} onChange={(e) => upd({ clientEmail: e.target.value })} className={inputCls} /></Field>
              <Field label="Adresse complète" className="md:col-span-2"><textarea rows={2} value={facture.clientAdresse} onChange={(e) => upd({ clientAdresse: e.target.value })} className={inputCls} /></Field>
              <Field label="SIRET (obligatoire B2B)"><input value={facture.clientSiret} onChange={(e) => upd({ clientSiret: e.target.value })} placeholder="14 chiffres" className={inputCls} /></Field>
              <Field label="N° TVA intracommunautaire"><input value={facture.clientTvaIntra} onChange={(e) => upd({ clientTvaIntra: e.target.value })} placeholder="FR..." className={inputCls} /></Field>
              <Field label="Plateforme PDP du client (e-invoicing 2026)" className="md:col-span-2"><input value={facture.clientPlateformeAgree || ''} onChange={(e) => upd({ clientPlateformeAgree: e.target.value })} placeholder="Ex: Sage Network, Pennylane PDP, Docaposte…" className={inputCls} /></Field>
            </div>
          </div>

          {/* Lignes */}
          <LignesFactureEditor lignes={facture.lignes} onChange={(lignes) => onChange({ ...facture, lignes })} />

          {/* Totaux */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="Remise globale (%)"><input type="number" step="0.1" value={facture.remisePct} onChange={(e) => upd({ remisePct: parseFloat(e.target.value) || 0 })} className={inputCls} /></Field>
              <Field label="Acompte déjà versé (€)"><input type="number" step="0.01" value={facture.acompteVerse} onChange={(e) => upd({ acompteVerse: parseFloat(e.target.value) || 0 })} className={inputCls} /></Field>
              <Field label="Mode de paiement">
                <select value={facture.modePaiement} onChange={(e) => upd({ modePaiement: e.target.value })} className={selectCls}>
                  {paiementModes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Conditions de paiement"><input value={facture.conditionsPaiement} onChange={(e) => upd({ conditionsPaiement: e.target.value })} className={inputCls} /></Field>
            </div>
            <Card className="p-4 bg-slate-950/40">
              <div className="space-y-1.5 text-sm">
                <Row label="Total HT" value={formatEUR(t.totalHT)} />
                {facture.remisePct > 0 && <Row label={`Remise (${facture.remisePct}%)`} value={`- ${formatEUR(t.remise)}`} />}
                <Row label="HT net" value={formatEUR(t.htNet)} />
                {totauxDocument(facture.lignes, facture.remisePct).tvaParTaux.map((x) => (
                  <Row key={x.taux} label={`TVA ${String(x.taux).replace('.', ',')} % (base ${formatEUR(x.base)})`} value={formatEUR(x.tva)} />
                ))}
                <Row label="TVA totale" value={formatEUR(t.tva)} />
                <div className="border-t border-slate-800 pt-2 mt-2"><Row label="Total TTC" value={formatEUR(t.ttc)} bold /></div>
                {facture.acompteVerse > 0 && <Row label="Acompte versé" value={`- ${formatEUR(facture.acompteVerse)}`} />}
                <Row label="Net à payer" value={formatEUR(t.restant)} bold accent />
              </div>
            </Card>
          </div>

          {/* Échéancier / traites */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <h3 className="text-[10px] uppercase tracking-widest text-slate-400">Échéancier — acompte, traites & solde</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Auto-rempli depuis la proposition liée. Modifiable ligne par ligne — apparaît sur la facture finale.
                  {echeancier.length > 0 && (
                    <span className="ml-2 text-slate-400">Total échéances : <strong className={echeancierEquilibre(echeancier.map((e) => Number(e.montant) || 0), t.ttc) ? 'text-emerald-300' : 'text-red-400'}>{formatEUR(echTotal)}</strong> / TTC {formatEUR(t.ttc)}
                      {!echeancierEquilibre(echeancier.map((e) => Number(e.montant) || 0), t.ttc) && <span className="text-red-400"> — écart de {formatEUR(round2(t.ttc - echTotal))}</span>}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {echeancier.length > 0 && !echeancierEquilibre(echeancier.map((e) => Number(e.montant) || 0), t.ttc) && (
                  <button type="button" onClick={() => { const last = echeancier.length - 1; const avant = echeancier.slice(0, last).reduce((s2, e) => s2 + (Number(e.montant) || 0), 0); updEch(last, { montant: round2(t.ttc - avant) }); }} className="text-xs text-emerald-400 hover:text-emerald-300">Répartir le solde sur la dernière échéance</button>
                )}
                {echeancier.length > 0 && (
                  <button type="button" onClick={resetEch} className="text-xs text-slate-400 hover:text-slate-200">Vider</button>
                )}
                <button type="button" onClick={addEch} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                  <Plus size={12} strokeWidth={1.5} /> Ajouter une échéance
                </button>
              </div>
            </div>
            {echeancier.length === 0 ? (
              <div className="text-xs text-slate-500 italic bg-slate-950/40 rounded-md p-3 border border-dashed border-slate-800">
                Aucune échéance définie — la facture utilisera le champ « Conditions de paiement » ci-dessus.
              </div>
            ) : (
              <div className="space-y-2">
                {echeancier.map((e, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-950/40 rounded-md p-2">
                    <input
                      value={e.label || ''}
                      onChange={(ev) => updEch(i, { label: ev.target.value })}
                      placeholder="Libellé (Acompte, Traite 1/3, Solde…)"
                      className={`${inputCls} col-span-5`}
                    />
                    <input
                      type="date"
                      value={e.date || ''}
                      onChange={(ev) => updEch(i, { date: ev.target.value })}
                      className={`${inputCls} col-span-3`}
                    />
                    <div className="col-span-3 relative">
                      <input
                        type="number"
                        step="0.01"
                        value={e.montant ?? ''}
                        onChange={(ev) => updEch(i, { montant: ev.target.value === '' ? null : parseFloat(ev.target.value) })}
                        placeholder="Montant"
                        className={`${inputCls} pr-7`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">€</span>
                    </div>
                    <button onClick={() => removeEch(i)} className="col-span-1 text-red-400 hover:text-red-300 p-1.5 justify-self-center">
                      <X size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* e-invoice flags */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Toggle checked={facture.factorXGenere} onChange={(v) => upd({ factorXGenere: v })} label="Factur-X généré" hint="PDF/A-3 + XML EN 16931" />
            <Toggle checked={facture.pdpTransmis} onChange={(v) => upd({ pdpTransmis: v })} label="PDP transmis" hint="Plateforme Dématérialisation Partenaire" />
            <Toggle checked={facture.chorusProTransmis} onChange={(v) => upd({ chorusProTransmis: v })} label="Chorus Pro" hint="Pour clients du secteur public" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] uppercase tracking-widest text-slate-400">Mentions légales</label>
              <button
                type="button"
                onClick={() => upd({ mentionsLegales: mentionsLegalesFor(facture.structure) })}
                className="text-[10px] text-amber-400 hover:text-amber-300 underline"
              >
                Réinitialiser selon la structure
              </button>
            </div>
            <textarea rows={3} value={facture.mentionsLegales} onChange={(e) => upd({ mentionsLegales: e.target.value })} className={inputCls} />
          </div>
          <Field label="Notes internes"><textarea rows={2} value={facture.notes} onChange={(e) => upd({ notes: e.target.value })} className={inputCls} /></Field>
        </div>
      </div>
      {showPreview && (
        <div className="hidden xl:block sticky top-8 my-8 w-[620px] shrink-0 h-[calc(100vh-4rem)] bg-white rounded-xl overflow-hidden border border-slate-700">
          <iframe title="Aperçu en direct" srcDoc={previewHtml} className="w-full h-full border-0" sandbox="" />
        </div>
      )}
    </div>
  );
}




/* ---------- Helpers UI ---------- */

const inputCls = 'w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50';
const selectCls = inputCls;

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value, bold, accent }: { label: string; value: string; bold?: boolean; accent?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={`${bold ? 'text-slate-200 font-medium' : 'text-slate-400'}`}>{label}</span>
      <span className={`${bold ? 'font-medium' : ''} ${accent ? 'text-amber-400' : 'text-slate-200'}`}>{value}</span>
    </div>
  );
}

function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <button onClick={() => onChange(!checked)} className={`p-3 rounded-lg border text-left transition-colors ${checked ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-slate-950/40 border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-sm font-medium ${checked ? 'text-emerald-300' : 'text-slate-300'}`}>{label}</span>
        {checked && <Check size={14} className="text-emerald-400" strokeWidth={2} />}
      </div>
      <div className="text-[10px] text-slate-500">{hint}</div>
    </button>
  );
}

/* ---------- HTML Print template ---------- */

function renderInvoiceHTML(f: Facture, em: import('../types').EmetteurInfo): string {
  const t = calcTotaux(f);
  const hl = (v: string | undefined | null): string => {
    const s = (v || '').toString();
    if (!s) return '<span class="todo">[Information à insérer]</span>';
    return escapeHtml(s).replace(/\[Information à insérer\]/g, '<span class="todo">[Information à insérer]</span>');
  };
  const lignesHTML = f.lignes.map((l) => {
    const tl = l.typeLigne || 'article';
    if (tl === 'saut_page') return `<tr style="page-break-after:always"><td colspan="5"></td></tr>`;
    if (tl === 'section') return `<tr><td colspan="5" style="font-weight:bold;background:#f9f6ef;color:#1a2332;text-transform:uppercase;letter-spacing:1px">${escapeHtml(l.description)}</td></tr>`;
    if (tl === 'commentaire') return `<tr><td colspan="5" style="font-style:italic;color:#555">${escapeHtml(l.description).replace(/\n/g, '<br>')}</td></tr>`;
    const ht = (l.quantite || 0) * (l.prixUnitaireHT || 0);
    const ref = l.reference ? `<span style="font-family:monospace;color:#888">${escapeHtml(l.reference)}</span> ` : '';
    const rem = l.remisePct ? `<br><span style="font-size:11px;color:#888">Prix ${formatEUR(l.prixBrutHT ?? l.prixUnitaireHT)} − remise ${String(l.remisePct).replace('.', ',')} %</span>` : '';
    return `<tr>
      <td>${ref}${escapeHtml(l.description).replace(/\n/g, '<br>')}${rem}</td>
      <td class="r">${l.quantite}${l.unite ? ' ' + escapeHtml(l.unite) : ''}</td>
      <td class="r">${formatEUR(l.prixUnitaireHT)}</td>
      <td class="r">${String(l.tvaPct).replace('.', ',')}%</td>
      <td class="r">${formatEUR(ht)}</td>
    </tr>`;
  }).join('');

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${f.numero}</title>
<style>
  *{box-sizing:border-box} body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1a1a1a;max-width:800px;margin:30px auto;padding:0 30px;font-size:13px;line-height:1.5}
  h1{font-size:28px;margin:0;color:#b8860b;letter-spacing:1px}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:2px solid #b8860b}
  .em{font-size:12px;line-height:1.6}
  .em strong{font-size:14px;color:#222}
  .meta{background:#f9f6ef;padding:15px;border-left:3px solid #b8860b;margin-bottom:20px}
  .meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:15px}
  .meta-grid div strong{display:block;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:3px}
  table.lignes{width:100%;border-collapse:separate;border-spacing:0;margin:20px 0}
  table.lignes th{background:#1a2332;color:#fff;text-align:left;padding:12px 10px;font-size:11px;text-transform:uppercase;letter-spacing:1px;line-height:1.3}
  table.lignes td{padding:14px 10px 12px;border-bottom:1px solid #eee;vertical-align:top}
  table.lignes tbody tr:first-child td{padding-top:16px}
  table{width:100%;border-collapse:collapse;margin:20px 0}
  td{padding:10px;vertical-align:top}
  td.r,th.r{text-align:right}
  .totals{margin-left:auto;width:300px;margin-top:20px}
  .totals tr td{border:none;padding:5px 10px}
  .totals .total{background:#b8860b;color:#fff;font-weight:bold;font-size:15px}
  .footer{margin-top:40px;padding-top:20px;border-top:1px solid #ddd;font-size:11px;color:#666}
  .badge{display:inline-block;padding:3px 8px;background:#e8f5e9;color:#2e7d32;border-radius:3px;font-size:10px;font-weight:bold}
  .todo{color:#dc2626;font-weight:bold;background:#fee2e2;padding:1px 4px;border-radius:2px}
</style></head><body>
<div class="header">
  <div style="display:flex;align-items:center;gap:14px">
    ${f.structure === 'cygnature' ? `<img src="${logoSrc(f.structure)}" alt="Cygnature Production" style="height:54px;width:auto"/>` : ''}
    <div>
      <h1>${f.type === 'Acompte' ? "FACTURE D'ACOMPTE" : f.type === 'Intermédiaire' ? 'FACTURE INTERMÉDIAIRE' : f.type === 'Solde' ? 'FACTURE DE SOLDE' : f.type.toUpperCase()}</h1>
      <div style="font-size:14px;color:#888;margin-top:5px">N° ${escapeHtml(f.numero)}</div>
      ${f.factorXGenere ? '<div class="badge" style="margin-top:8px">FACTUR-X / e-invoice</div>' : ''}
    </div>
  </div>
  <div class="em" style="text-align:right">
    <strong>${hl(em.raisonSociale)}</strong><br>
    ${hl(em.adresse).replace(/\n/g, '<br>')}<br>
    SIRET : ${hl(em.siret)}<br>
    TVA : ${hl(em.tvaIntra)}<br>
    ${em.email ? `${hl(em.email)}<br>` : ''}
    ${em.tel ? `${hl(em.tel)}` : ''}
  </div>
</div>

<div class="meta">
  <div class="meta-grid">
    <div>
      <strong>Facturé à</strong>
      <div><b>${escapeHtml(f.clientNom)}</b></div>
      <div>${escapeHtml(f.clientAdresse).replace(/\n/g, '<br>')}</div>
      ${f.clientSiret ? `<div>SIRET : ${escapeHtml(f.clientSiret)}</div>` : ''}
      ${f.clientTvaIntra ? `<div>TVA : ${escapeHtml(f.clientTvaIntra)}</div>` : ''}
    </div>
    <div style="text-align:right">
      <strong>Émise le</strong><div>${formatDateFR(f.dateEmission)}</div>
      <div style="margin-top:8px"><strong>Échéance</strong><div>${formatDateFR(f.dateEcheance)}</div></div>
      <div style="margin-top:8px"><strong>Date prestation</strong><div>${formatDateFR(f.datePrestation)}</div></div>
      ${f.prefixDevisOrigine ? `<div style="margin-top:8px"><strong>Réf. devis</strong><div>${escapeHtml(f.prefixDevisOrigine)}</div></div>` : ''}
    </div>
  </div>
</div>

<table class="lignes">
  <thead><tr><th>Description</th><th class="r">Qté</th><th class="r">PU HT</th><th class="r">TVA</th><th class="r">Total HT</th></tr></thead>
  <tbody>${lignesHTML}</tbody>
</table>

<table class="totals">
  <tr><td>Total HT</td><td class="r">${formatEUR(t.totalHT)}</td></tr>
  ${f.remisePct > 0 ? `<tr><td>Remise ${f.remisePct}%</td><td class="r">- ${formatEUR(t.remise)}</td></tr>` : ''}
  ${totauxDocument(f.lignes, f.remisePct).tvaParTaux.map((x) => `<tr><td>TVA ${String(x.taux).replace('.', ',')} %</td><td class="r">${formatEUR(x.tva)}</td></tr>`).join('')}
  <tr class="total"><td>TOTAL TTC</td><td class="r">${formatEUR(t.ttc)}</td></tr>
  ${f.acompteVerse > 0 ? `<tr><td>Acompte versé</td><td class="r">- ${formatEUR(f.acompteVerse)}</td></tr><tr class="total"><td>NET À PAYER</td><td class="r">${formatEUR(t.restant)}</td></tr>` : ''}
</table>

<div class="footer">
  <p style="margin:0 0 6px"><strong>Mode de paiement :</strong> ${escapeHtml(f.modePaiement || '—')}</p>
  ${(f.echeancier && f.echeancier.length) ? `
  <p style="margin:0 0 4px"><strong>Échéancier :</strong></p>
  <ul style="margin:0 0 8px 18px;padding:0">
    ${f.echeancier.map((e) => {
      const lbl = e?.label || 'Échéance';
      const dt = e?.date ? formatDateFR(e.date) : '';
      const mt = (e?.montant != null && !isNaN(Number(e.montant))) ? formatEUR(Number(e.montant)) : '';
      return `<li style="margin:2px 0">${escapeHtml(lbl)}${dt ? ` — <em>le ${escapeHtml(dt)}</em>` : ''}${mt ? ` — <strong>${escapeHtml(mt)}</strong>` : ''}</li>`;
    }).join('')}
  </ul>` : (f.conditionsPaiement ? `<p style="margin:0 0 8px"><strong>Modalités :</strong> ${escapeHtml(f.conditionsPaiement).replace(/\n/g, '<br>').replace(/ ; /g, '<br>')}</p>` : '')}
  <p style="margin:0 0 4px"><strong>IBAN :</strong> ${hl(em.iban)} — <strong>BIC :</strong> ${hl(em.bic)}</p>
  <p style="margin-top:15px;font-size:10px;color:#888">${escapeHtml(f.mentionsLegales)}</p>
  <p style="font-size:10px;color:#888">RCS ${hl(em.rcs)} — Capital ${hl(em.capital)} — APE ${hl(em.ape)}</p>
</div>
</body></html>`;
}

function escapeHtml(s: string): string {
  return (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/* ---------- Factur-X / UBL minimal XML (EN 16931) ---------- */

function renderFacturXML(f: Facture, em: import('../types').EmetteurInfo): string {
  const t = calcTotaux(f);
  const esc = (s: string) => escapeHtml(s);
  const lignesXML = f.lignes.filter((l) => estChiffree(l.typeLigne)).map((l, i) => {
    const ht = (l.quantite || 0) * (l.prixUnitaireHT || 0);
    return `  <ram:IncludedSupplyChainTradeLineItem>
    <ram:AssociatedDocumentLineDocument><ram:LineID>${i + 1}</ram:LineID></ram:AssociatedDocumentLineDocument>
    <ram:SpecifiedTradeProduct><ram:Name>${esc(l.description)}</ram:Name></ram:SpecifiedTradeProduct>
    <ram:SpecifiedLineTradeAgreement><ram:NetPriceProductTradePrice><ram:ChargeAmount>${l.prixUnitaireHT.toFixed(2)}</ram:ChargeAmount></ram:NetPriceProductTradePrice></ram:SpecifiedLineTradeAgreement>
    <ram:SpecifiedLineTradeDelivery><ram:BilledQuantity unitCode="C62">${l.quantite}</ram:BilledQuantity></ram:SpecifiedLineTradeDelivery>
    <ram:SpecifiedLineTradeSettlement>
      <ram:ApplicableTradeTax><ram:TypeCode>VAT</ram:TypeCode><ram:CategoryCode>S</ram:CategoryCode><ram:RateApplicablePercent>${l.tvaPct}</ram:RateApplicablePercent></ram:ApplicableTradeTax>
      <ram:SpecifiedTradeSettlementLineMonetarySummation><ram:LineTotalAmount>${ht.toFixed(2)}</ram:LineTotalAmount></ram:SpecifiedTradeSettlementLineMonetarySummation>
    </ram:SpecifiedLineTradeSettlement>
  </ram:IncludedSupplyChainTradeLineItem>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<rsm:CrossIndustryInvoice
  xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100">
  <rsm:ExchangedDocumentContext>
    <ram:GuidelineSpecifiedDocumentContextParameter><ram:ID>urn:cen.eu:en16931:2017#conformant#urn:factur-x.eu:1p0:basic</ram:ID></ram:GuidelineSpecifiedDocumentContextParameter>
  </rsm:ExchangedDocumentContext>
  <rsm:ExchangedDocument>
    <ram:ID>${esc(f.numero)}</ram:ID>
    <ram:TypeCode>${f.type === 'Avoir' ? '381' : '380'}</ram:TypeCode>
    <ram:IssueDateTime><udt:DateTimeString format="102">${f.dateEmission.replace(/-/g, '')}</udt:DateTimeString></ram:IssueDateTime>
  </rsm:ExchangedDocument>
  <rsm:SupplyChainTradeTransaction>
${lignesXML}
    <ram:ApplicableHeaderTradeAgreement>
      <ram:SellerTradeParty>
        <ram:Name>${esc(em.raisonSociale)}</ram:Name>
        <ram:SpecifiedLegalOrganization><ram:ID schemeID="0002">${esc(em.siret)}</ram:ID></ram:SpecifiedLegalOrganization>
        <ram:PostalTradeAddress><ram:CountryID>FR</ram:CountryID></ram:PostalTradeAddress>
        <ram:SpecifiedTaxRegistration><ram:ID schemeID="VA">${esc(em.tvaIntra)}</ram:ID></ram:SpecifiedTaxRegistration>
      </ram:SellerTradeParty>
      <ram:BuyerTradeParty>
        <ram:Name>${esc(f.clientNom)}</ram:Name>
        ${f.clientSiret ? `<ram:SpecifiedLegalOrganization><ram:ID schemeID="0002">${esc(f.clientSiret)}</ram:ID></ram:SpecifiedLegalOrganization>` : ''}
        <ram:PostalTradeAddress><ram:CountryID>FR</ram:CountryID></ram:PostalTradeAddress>
        ${f.clientTvaIntra ? `<ram:SpecifiedTaxRegistration><ram:ID schemeID="VA">${esc(f.clientTvaIntra)}</ram:ID></ram:SpecifiedTaxRegistration>` : ''}
      </ram:BuyerTradeParty>
    </ram:ApplicableHeaderTradeAgreement>
    <ram:ApplicableHeaderTradeDelivery>
      <ram:ActualDeliverySupplyChainEvent><ram:OccurrenceDateTime><udt:DateTimeString format="102">${f.datePrestation.replace(/-/g, '')}</udt:DateTimeString></ram:OccurrenceDateTime></ram:ActualDeliverySupplyChainEvent>
    </ram:ApplicableHeaderTradeDelivery>
    <ram:ApplicableHeaderTradeSettlement>
      <ram:InvoiceCurrencyCode>EUR</ram:InvoiceCurrencyCode>
      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>
        <ram:LineTotalAmount>${t.totalHT.toFixed(2)}</ram:LineTotalAmount>
        <ram:TaxBasisTotalAmount>${t.htNet.toFixed(2)}</ram:TaxBasisTotalAmount>
        <ram:TaxTotalAmount currencyID="EUR">${t.tva.toFixed(2)}</ram:TaxTotalAmount>
        <ram:GrandTotalAmount>${t.ttc.toFixed(2)}</ram:GrandTotalAmount>
        <ram:DuePayableAmount>${t.restant.toFixed(2)}</ram:DuePayableAmount>
      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>
    </ram:ApplicableHeaderTradeSettlement>
  </rsm:SupplyChainTradeTransaction>
</rsm:CrossIndustryInvoice>`;
}
